import { ValidationResponse } from "@/components/google-map/helpers/validateProjectSize"
import { UploadDataItem } from "@/pages/application/content/Documents"
import { CreateAoiData, CreateAppData, CreatedFeatures } from "@/types"
import { FeatureCollection } from "geojson"
import moment from "moment"
import { Dispatch, SetStateAction, createContext, useContext } from "react"

type DrawerContentType =
  | "create-profile"
  | "create-company"
  | "company-info"
  | "profile-review"
  | "quote-review"
  | "approval-review"
  | "email-history"
  | "email-review"
  | "generate-approval-document"
  | "generate-aoi-approval"
  | "generate-enforcement-invoice"
  | "licence-update-request"
  | ""

export type ModalTypes =
  | "simple"
  | "delete-task"
  | "apply-for-renewal"
  | "create-user"
  | "follow-up"
  | "follow-up-renewal"
  | "final-notification"
  | "resubmit-application"
  | "company-info"
  | "add-approval-item"
  | "register-success"
  | "providers-attach"
  | "create-application-info"
  | "create-application-success"
  | "create-application"
  | "add-application"
  | "create-payment"
  | "application-report"
  | "application-initial-info"
  | "application-assign-confirmation"
  | "map-saving-info"
  | "application-assign-step-01"
  | "application-assign-step-02"
  | "application-info"
  | "project-created-success"
  | "request-info"
  | "approve-application"
  | "quote-response"
  | "create-survey"
  | "approve-now"
  | "payment-options"
  | "company-update-request-success"
  | "payment-approve"
  | "invoice-approve"
  | "upload-receipt"
  | "assign-user"
  | "create-company"
  | "send-quotation"
  | "confirm-quotation"
  | "create-quotation-item"
  | "user-profile-review"
  | "delete-deposit-confirmation"
  | "delete-email-confirmation"
  | "delete-enforcement-confirmation"
  | "archive-project-confirmation"
  | "archive-quote-request-confirmation"
  | "remove-quote-request-confirmation"
  | "archive-application-confirmation"
  | "restore-application-confirmation"
  | "restore-project-confirmation"
  | "create-kickoff-invite"
  | "create-kickoff-record"
  | "create-deposit-record"
  | "create-completion-certificate"
  | "delete-kickoff-record"
  | "delete-geometry-item"
  | "delete-completion-certificate"
  | "delete-kickoff-invite"
  | "agent-assign-to-app"
  | "agent-assign-to-approval"
  | "agent-assign-notification"
  | "change-agent-status"
  | "change-agent-approval-status"
  | "change-geometry-category"
  | "change-geometry-provinces"
  | "change-geometry-status"
  | "add-document-type"
  | "duplicate-application"
  | "confirm-delivery-approval"
  | "change-approval-status"
  | "resubmit-approval"
  | "delivery-note"
  | "delivery-group-details"
  | "view-email"
  | "geometry-settings"
  | "approval-letter-preview"
  | "unassign-approval-agent-confirmation"
  | "unassign-app-agent-confirmation"
  | "level-of-service"
  | "upload-details"
  | "add-geometry"
  | "create-enforcement"
  | "assistance-info"
  | "assistance-info-success"
  | "change-assistance-request-status"
  | "change-license-update-request-status"
  | "delete-assistance-request"
  | "delete-license-update-request"
  | "update-company-info"
  | "auto-approval-review"
  | ""
export type SimpleModalSettings = {
  buttonLink?: string
  buttonText?: string
  content: string
  title: string
  onClick?: () => void
  isShowCancel?: boolean
}
export type UserLevelOfApproval = {
  userId: string
  level: string
}[]
type Mode = "request-processing" | "unsupported-geometry" | string | null
type SidebarMode = "default" | "user-info" | "app-info" | "project-info" | "project-overview"
export type PageViewMode = "cards" | "table"
export type ApprovalCategory = "approval" | "renewal"
export type AssignModalMode = "application" | "approval"
export type GuideTopicsTypes =
  | "start-flow-description"
  | "how-to-optimize-your-project-geometry"
  | "how-to-mark-the-map"
export type ApprovalLetterInfo = {
  name: string
  id: string
  url: string
  type?: string
}
export type DeliveryConfirmationMode = "single" | "multiple"
export type ModalMode = "default" | "fullscreen"
type ContextProps = {
  assignModalMode: AssignModalMode
  setAssignModalMode: (mode: AssignModalMode) => void
  createAppCurrentStep: number
  currentApplicationId: Maybe<string>
  currentOrgSlug: Maybe<string>
  setCurrentOrgSlug: Dispatch<SetStateAction<Maybe<string>>>
  currentEmailMessageId: string | null
  /** Email Log → "Add to Approval" for an email's text: the generated PDF the approval form adds to the documents */
  emailBodyDocument: ApprovalLetterInfo | null
  setEmailBodyDocument: Dispatch<SetStateAction<ApprovalLetterInfo | null>>
  currentCompanyId: Maybe<string>
  currentLevelOfService: Maybe<EnumProjectLevelofservices>
  currentAoi: Maybe<AoiEntity>
  setCurrentAoi: Dispatch<SetStateAction<Maybe<AoiEntity>>>
  currentId: string
  approvalLetterInfo: ApprovalLetterInfo | null
  url: string | null
  imageId: string | null
  currentApprovalItemId: string
  documentInfo: Record<string, string>
  isModalOpen: boolean
  isLoading: boolean
  isDrawerOpen: boolean
  drawerWidth: string | number | null
  isPaymentCreated: boolean
  isApprovalMode: boolean
  isAffected: boolean
  isApproved: boolean
  isNewEmails: boolean
  setIsNewEmails: Dispatch<SetStateAction<boolean>>
  setIsApprovalMode: Dispatch<SetStateAction<boolean>>
  isReadyForReview: boolean
  createdContactIds: string[]
  closedInfoPopupAppIds: string[]
  setCreatedContactIds: Dispatch<SetStateAction<string[]>>
  setCurrentLevelOfService: Dispatch<SetStateAction<Maybe<EnumProjectLevelofservices>>>
  setClosedInfoPopupAppIds: Dispatch<SetStateAction<string[]>>
  locationConfirmationFormStep: string
  modalSettings: SimpleModalSettings
  modalType: ModalTypes
  drawerType: DrawerContentType
  modalWidth: null | string | number
  setModalWidth: Dispatch<SetStateAction<number | string | null>>
  modalMode: ModalMode
  isFullScreen: boolean
  setIsFullScreen: Dispatch<SetStateAction<boolean>>
  setModalMode: Dispatch<SetStateAction<ModalMode>>
  setDrawerWidth: Dispatch<SetStateAction<string | number | null>>
  modalTopOffset: null | number
  setModalTopOffset: Dispatch<SetStateAction<number | null>>
  setApprovalLetterInfo: Dispatch<SetStateAction<ApprovalLetterInfo | null>>
  setUrl: Dispatch<SetStateAction<string | null>>
  setImageId: Dispatch<SetStateAction<string | null>>
  setCurrentEmailMessageId: Dispatch<SetStateAction<string | null>>
  setIsPaymentCreated: Dispatch<SetStateAction<boolean>>
  setIsAffected: Dispatch<SetStateAction<boolean>>
  setIsApproved: Dispatch<SetStateAction<boolean>>
  setIsLoading: Dispatch<SetStateAction<boolean>>
  setIsReadyForReview: Dispatch<SetStateAction<boolean>>
  setCreateAppCurrentStep: Dispatch<SetStateAction<number>>
  setCurrentApplicationId: Dispatch<SetStateAction<Maybe<string>>>
  setCurrentCompanyId: Dispatch<SetStateAction<Maybe<string>>>
  setCurrentId: Dispatch<SetStateAction<string>>
  setCurrentApprovalItemId: Dispatch<SetStateAction<string>>
  setDocumentInfo: Dispatch<SetStateAction<Record<string, string>>>
  setIsModalOpen: Dispatch<SetStateAction<boolean>>
  setIsDrawerOpen: Dispatch<SetStateAction<boolean>>
  setLocationConfirmationFormStep: Dispatch<SetStateAction<string>>
  setModalSettings: Dispatch<SetStateAction<SimpleModalSettings>>
  setModalType: Dispatch<SetStateAction<ModalTypes>>
  setDrawerType: Dispatch<SetStateAction<DrawerContentType>>
  setSidebarMode: Dispatch<SetStateAction<SidebarMode>>
  setTriggerRefetch: Dispatch<SetStateAction<boolean | null>>
  sidebarMode: SidebarMode
  triggerRefetch: boolean | null
  userLevelOfApproval: UserLevelOfApproval
  setUserLevelOfApproval: Dispatch<SetStateAction<UserLevelOfApproval>>
  mode: Mode
  setMode: Dispatch<SetStateAction<Mode>>
  pageViewMode: PageViewMode
  setPageViewMode: Dispatch<SetStateAction<PageViewMode>>
  tabActiveKey: string
  setTabActiveKey: Dispatch<SetStateAction<string>>
  approvalCategory: ApprovalCategory
  setApprovalCategory: Dispatch<SetStateAction<ApprovalCategory>>
  expiryDate: moment.Moment | null
  setExpiryDate: Dispatch<SetStateAction<moment.Moment | null>>
  currentApprovalOrgId: string
  setCurrentApprovalOrgId: Dispatch<SetStateAction<string>>
  totalAppNewNotifications: Maybe<number>
  setTotalAppNewNotifications: Dispatch<SetStateAction<Maybe<number>>>
  totalNewNotifications: Maybe<number>
  setTotalNewNotifications: Dispatch<SetStateAction<Maybe<number>>>
  projectGeometryInfo: ValidationResponse | null
  setProjectGeometryInfo: Dispatch<SetStateAction<ValidationResponse | null>>
  projectFeatures: CreatedFeatures | null
  setProjectFeatures: Dispatch<SetStateAction<CreatedFeatures | null>>
  projectGeoJson: FeatureCollection | null
  setProjectGeoJson: Dispatch<SetStateAction<FeatureCollection | null>>
  kmlUploadedId: string | null
  setKmlUploadedId: Dispatch<SetStateAction<string | null>>
  isGuideOpened: boolean
  setIsGuideOpened: Dispatch<SetStateAction<boolean>>
  openedGuideTopic: GuideTopicsTypes
  setOpenedGuideTopic: Dispatch<SetStateAction<GuideTopicsTypes>>
  applicationSource: string
  setApplicationSource: Dispatch<SetStateAction<string>>
  isGetReportMode: boolean
  setIsGetReportMode: Dispatch<SetStateAction<boolean>>
  currentCompany: Maybe<CompanyEntity>
  setCurrentCompany: Dispatch<SetStateAction<Maybe<CompanyEntity>>>
  currentProject: Maybe<ProjectEntity>
  setCurrentProject: Dispatch<SetStateAction<Maybe<ProjectEntity>>>
  createAppData: Maybe<Partial<CreateAppData>>
  setCreateAppData: Dispatch<SetStateAction<Maybe<Partial<CreateAppData>>>>
  createAoiData: Maybe<Partial<CreateAoiData>>
  setCreateAoiData: Dispatch<SetStateAction<Maybe<Partial<CreateAoiData>>>>
  navigateLink: Maybe<string>
  setNavigateLink: Dispatch<SetStateAction<Maybe<string>>>
  siteContacts: Maybe<string[]>
  setSiteContacts: Dispatch<SetStateAction<Maybe<string[]>>>
  currentApprovalItems: Maybe<ApprovalEntity>[] | null
  setCurrentApprovalItems: Dispatch<SetStateAction<Maybe<ApprovalEntity>[] | null>>
  deliveryApplications: Maybe<ApplicationEntity>[] | null
  setDeliveryApplications: Dispatch<SetStateAction<Maybe<ApplicationEntity>[] | null>>
  currentReasonForRejection: Maybe<EnumAoiReasonforrejection | null>
  setCurrentReasonForRejection: Dispatch<SetStateAction<Maybe<EnumAoiReasonforrejection | null>>>
}
export const defaultValues: ContextProps = {
  currentAoi: null,
  setCurrentAoi: () => null,
  isApproved: false,
  setIsApproved: () => null,
  currentProject: null,
  setCurrentProject: () => null,
  currentCompany: null,
  setCurrentCompany: () => null,
  isGetReportMode: false,
  setIsGetReportMode: () => null,
  isNewEmails: false,
  setIsNewEmails: () => null,
  applicationSource: "",
  setApplicationSource: () => null,
  openedGuideTopic: "start-flow-description",
  setOpenedGuideTopic: () => null,
  isGuideOpened: false,
  setIsGuideOpened: () => null,
  kmlUploadedId: null,
  setKmlUploadedId: () => null,
  currentOrgSlug: null,
  setCurrentOrgSlug: () => null,
  projectGeoJson: null,
  setProjectGeoJson: () => null,
  projectFeatures: null,
  setProjectFeatures: () => null,
  projectGeometryInfo: null,
  setProjectGeometryInfo: () => null,
  totalAppNewNotifications: 0,
  setTotalAppNewNotifications: () => null,
  totalNewNotifications: 0,
  setTotalNewNotifications: () => null,
  currentEmailMessageId: "",
  setCurrentEmailMessageId: () => null,
  emailBodyDocument: null,
  setEmailBodyDocument: () => null,
  currentLevelOfService: "full_wayleave",
  setCurrentLevelOfService: () => null,
  createdContactIds: [],
  setCreatedContactIds: () => null,
  currentApprovalOrgId: "",
  setCurrentApprovalOrgId: () => null,
  isApprovalMode: false,
  setIsApprovalMode: () => null,
  assignModalMode: "application",
  setAssignModalMode: () => null,
  expiryDate: null,
  setExpiryDate: () => null,
  sidebarMode: "default",
  closedInfoPopupAppIds: [],
  setClosedInfoPopupAppIds: () => null,
  setSidebarMode: () => null,
  approvalLetterInfo: null,
  setApprovalLetterInfo: () => null,
  url: "",
  imageId: "",
  modalType: "",
  drawerType: "",
  modalWidth: null,
  drawerWidth: null,
  isAffected: false,
  setModalWidth: () => null,
  setModalMode: () => null,
  isFullScreen: false,
  setIsFullScreen: () => null,
  modalMode: "default",
  setUrl: () => null,
  setImageId: () => null,
  setDrawerWidth: () => null,
  modalTopOffset: null,
  setModalTopOffset: () => null,
  setModalType: () => null,
  setDrawerType: () => null,
  isModalOpen: false,
  isDrawerOpen: false,
  isLoading: false,
  isReadyForReview: false,
  isPaymentCreated: false,
  setIsLoading: () => null,
  setIsPaymentCreated: () => null,
  setIsReadyForReview: () => null,
  setIsModalOpen: () => null,
  setIsDrawerOpen: () => null,
  locationConfirmationFormStep: "1",
  setLocationConfirmationFormStep: () => null,
  createAppCurrentStep: 1,
  setCreateAppCurrentStep: () => null,
  triggerRefetch: false,
  setTriggerRefetch: () => null,
  modalSettings: { title: "", content: "", isShowCancel: true },
  setModalSettings: () => null,
  currentApplicationId: "",
  currentCompanyId: null,
  setCurrentApplicationId: () => null,
  setCurrentCompanyId: () => null,
  currentId: "",
  setCurrentId: () => null,
  currentApprovalItemId: "",
  setCurrentApprovalItemId: () => null,
  currentApprovalItems: null,
  setCurrentApprovalItems: () => null,
  deliveryApplications: null,
  setDeliveryApplications: () => null,
  setIsAffected: () => null,
  documentInfo: {},
  setDocumentInfo: () => null,
  userLevelOfApproval: [],
  setUserLevelOfApproval: () => null,
  mode: null,
  setMode: () => null,
  pageViewMode: "table",
  setPageViewMode: () => null,
  tabActiveKey: "1",
  setTabActiveKey: () => null,
  approvalCategory: "approval",
  setApprovalCategory: () => null,
  createAppData: null,
  setCreateAppData: () => null,
  createAoiData: null,
  setCreateAoiData: () => null,
  navigateLink: "",
  setNavigateLink: () => null,
  siteContacts: [],
  setSiteContacts: () => null,
  currentReasonForRejection: null,
  setCurrentReasonForRejection: () => null,
}
const GeneralContext = createContext(defaultValues)
const useGeneralContext = (): ContextProps => useContext(GeneralContext)

export { GeneralContext, useGeneralContext }
